/**
 * Procedural 8-bit audio engine using Web Audio API.
 * - Two independent toggles: MUSIC and SFX (persisted in localStorage)
 * - Menu music: happy, up-tempo major pentatonic loop (Start/HowToPlay/Character)
 * - Month music: slightly different loops per month
 * - SFX: short procedural blips/zaps/dings
 */

let ctx = null;
let musicGain = null;
let sfxGain = null;
let musicInterval = null;
let musicMode = null; // "menu" | "month-N"

let musicEnabled = false;
let sfxEnabled = false;

const MUSIC_KEY = "mt_music_enabled";
const SFX_KEY = "mt_sfx_enabled";

export function isMusicEnabled() { return musicEnabled; }
export function isSfxEnabled() { return sfxEnabled; }

export function setMusicEnabled(on) {
  musicEnabled = !!on;
  try { localStorage.setItem(MUSIC_KEY, on ? "1" : "0"); } catch {}
  if (musicGain) musicGain.gain.value = on ? 0.10 : 0;
  if (!on) stopMusic();
}

export function setSfxEnabled(on) {
  sfxEnabled = !!on;
  try { localStorage.setItem(SFX_KEY, on ? "1" : "0"); } catch {}
  if (sfxGain) sfxGain.gain.value = on ? 0.22 : 0;
}

export function loadAudioPreference() {
  try {
    musicEnabled = localStorage.getItem(MUSIC_KEY) === "1";
    sfxEnabled = localStorage.getItem(SFX_KEY) === "1";
  } catch {
    musicEnabled = false;
    sfxEnabled = false;
  }
  return { music: musicEnabled, sfx: sfxEnabled };
}

function ensureCtx() {
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    musicGain = ctx.createGain();
    musicGain.gain.value = musicEnabled ? 0.10 : 0;
    musicGain.connect(ctx.destination);
    sfxGain = ctx.createGain();
    sfxGain.gain.value = sfxEnabled ? 0.22 : 0;
    sfxGain.connect(ctx.destination);
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

function blip({ freq = 440, duration = 0.12, type = "square", gain = 0.5, slideTo = null, attack = 0.005, release = 0.05, channel = "sfx" }) {
  const c = ensureCtx();
  if (!c) return;
  const dest = channel === "music" ? musicGain : sfxGain;
  if (!dest) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, c.currentTime);
  if (slideTo !== null) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(slideTo, 30), c.currentTime + duration);
  }
  g.gain.setValueAtTime(0.0001, c.currentTime);
  g.gain.exponentialRampToValueAtTime(gain, c.currentTime + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + duration + release);
  osc.connect(g);
  g.connect(dest);
  osc.start();
  osc.stop(c.currentTime + duration + release + 0.05);
}

function _sfx(opts) {
  if (!sfxEnabled) return;
  blip({ ...opts, channel: "sfx" });
}

export const sfx = {
  click: () => _sfx({ freq: 720, duration: 0.05, gain: 0.4 }),
  select: () => _sfx({ freq: 880, duration: 0.08, gain: 0.5, slideTo: 1320 }),
  back: () => _sfx({ freq: 520, duration: 0.07, slideTo: 320, gain: 0.4 }),
  buy: () => {
    _sfx({ freq: 660, duration: 0.06, gain: 0.4 });
    setTimeout(() => _sfx({ freq: 990, duration: 0.08, gain: 0.4 }), 70);
  },
  spin: () => _sfx({ freq: 220, duration: 0.5, slideTo: 1200, gain: 0.35 }),
  win: () => {
    [523, 659, 784, 1046].forEach((f, i) => setTimeout(() => _sfx({ freq: f, duration: 0.12, gain: 0.5 }), i * 110));
  },
  lose: () => {
    [392, 311, 261, 196].forEach((f, i) => setTimeout(() => _sfx({ freq: f, duration: 0.15, gain: 0.5 }), i * 130));
  },
  ding: () => _sfx({ freq: 1320, duration: 0.16, gain: 0.45, type: "triangle" }),
  zap: () => _sfx({ freq: 140, duration: 0.18, slideTo: 60, gain: 0.45, type: "sawtooth" }),
  cash: () => {
    [880, 1175, 1568, 2093].forEach((f, i) => setTimeout(() => _sfx({ freq: f, duration: 0.08, gain: 0.4, type: "triangle" }), i * 60));
  },
  ability: () => {
    [392, 523, 659, 784, 988].forEach((f, i) => setTimeout(() => _sfx({ freq: f, duration: 0.08, gain: 0.45 }), i * 60));
  },
  bootstrap: () => {
    [196, 220, 196, 220].forEach((f, i) => setTimeout(() => _sfx({ freq: f, duration: 0.15, gain: 0.5, type: "sawtooth" }), i * 180));
  },
};

// Major pentatonic scales (happy, uptempo for menu; slight variation per month)
const SCALES = [
  [523.25, 587.33, 659.25, 783.99, 880.00, 1046.50], // C5 menu — bright/happy
  [293.66, 349.23, 440.00, 493.88, 587.33],
  [220.00, 261.63, 329.63, 392.00, 440.00],
  [196.00, 246.94, 293.66, 369.99, 440.00],
  [261.63, 311.13, 349.23, 392.00, 466.16],
];

function patternFromSeed(seed, len = 8) {
  let v = seed * 9301 + 49297;
  const out = [];
  for (let i = 0; i < len; i++) {
    v = (v * 9301 + 49297) % 233280;
    out.push(v % 5);
  }
  return out;
}

// Menu loop — happier, slightly bouncier with a bass thump on every 4th
const MENU_PATTERN = [0, 2, 4, 2, 1, 3, 5, 3, 0, 4, 2, 5, 1, 3, 2, 0];

export function startMenuMusic() {
  if (!musicEnabled) return;
  const c = ensureCtx();
  if (!c) return;
  if (musicMode === "menu") return; // already playing
  stopMusic();
  musicMode = "menu";
  const scale = SCALES[0];
  let step = 0;
  const tempo = 170; // ms per step — uptempo
  musicInterval = setInterval(() => {
    if (!musicEnabled) return;
    const n = MENU_PATTERN[step % MENU_PATTERN.length];
    const f = scale[n];
    blip({ freq: f, duration: 0.13, gain: 0.22, type: "square", channel: "music" });
    if (step % 4 === 0) {
      blip({ freq: scale[0] / 2, duration: 0.18, gain: 0.18, type: "triangle", channel: "music" });
    }
    if (step % 8 === 4) {
      blip({ freq: scale[2] * 2, duration: 0.08, gain: 0.10, type: "square", channel: "music" });
    }
    step++;
  }, tempo);
}

export function startMonthMusic(monthIndex = 0) {
  if (!musicEnabled) return;
  const c = ensureCtx();
  if (!c) return;
  const mode = `month-${monthIndex}`;
  if (musicMode === mode) return;
  stopMusic();
  musicMode = mode;
  const scale = SCALES[(monthIndex % (SCALES.length - 1)) + 1] || SCALES[1];
  const pattern = patternFromSeed(monthIndex + 1, 8);
  let step = 0;
  const tempo = 240 + (monthIndex % 4) * 15;
  musicInterval = setInterval(() => {
    if (!musicEnabled) return;
    const n = pattern[step % pattern.length];
    const f = scale[n];
    blip({ freq: f, duration: 0.13, gain: 0.18, type: "square", channel: "music" });
    if (step % 4 === 0) {
      blip({ freq: f / 2, duration: 0.13, gain: 0.13, type: "triangle", channel: "music" });
    }
    step++;
  }, tempo);
}

export function stopMusic() {
  if (musicInterval) {
    clearInterval(musicInterval);
    musicInterval = null;
  }
  musicMode = null;
}

// Back-compat shim for older imports
export const isAudioEnabled = isMusicEnabled;
export const setAudioEnabled = (on) => { setMusicEnabled(on); setSfxEnabled(on); };
export const startMusic = startMonthMusic;
